import React, { useState } from 'react';
import { InventoryBalanceItem, InventoryLotQualityAlert } from '../types';
import './InventoryAttentionPanel.css';

interface Props {
  items: InventoryBalanceItem[];
  alerts: InventoryLotQualityAlert[];
  loading: boolean;
  error: string | null;
  onLots: (productId: number) => void;
  onMovements: (productId: number) => void;
}

export default function InventoryAttentionPanel({ items, alerts, loading, error, onLots, onMovements }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const rows = items.map(item => {
    const lots = alerts.filter(lot => lot.productId === item.productId);
    const causes: Array<{ priority: number; text: string }> = [];
    const amount = (quantity: number, unit = item.weightType) => `${quantity.toFixed(3)} ${unit}`;
    if (item.balance < 0) causes.push({ priority: 1, text: `Від’ємний залишок: ${amount(item.balance)} — перевірте рухи.` });
    const lotCauses: Array<[number, string, (lot: InventoryLotQualityAlert) => boolean]> = [
      [1, 'Відкликано', lot => lot.qualityStatus === 'Recalled'],
      [1, 'Прострочено', lot => lot.daysUntilExpiration !== null && lot.daysUntilExpiration < 0],
      [2, 'Карантин', lot => lot.qualityStatus === 'Hold'],
      [3, 'Придатність закінчується за 0–14 днів', lot => lot.daysUntilExpiration !== null && lot.daysUntilExpiration >= 0 && lot.daysUntilExpiration <= 14],
    ];
    lotCauses.forEach(([priority, label, matches]) => {
      lots.filter(matches).forEach(lot => causes.push({
        priority,
        text: `${label}: ${lot.lotNumber || `накладна #${lot.receiptId}`} · ${amount(lot.remainingQuantity, lot.weightType)}${lot.expirationDate ? ` · до ${lot.expirationDate.slice(0, 10)}` : ''}${lot.qualityReason ? ` · ${lot.qualityReason}` : ''}`,
      }));
    });
    if (item.unpostedWriteOffQuantity > 0) causes.push({
      priority: 2, text: `Непроведене списання (нестача): ${amount(item.unpostedWriteOffQuantity)} — перевірте прихід і рухи; це не чернетка й не борг майбутніх приходів.`,
    });
    if (item.balance >= 0 && item.balance < 5) causes.push({
      priority: 3, text: `Низький залишок: ${amount(item.balance)} — наявний індикатор 0 ≤ залишок < 5, не мінімальний норматив.`,
    });
    if (item.untracedWriteOffQuantity > 0) causes.push({
      priority: 4, text: `Простежуваність: ${amount(item.untracedWriteOffQuantity)} списань / коригувань без прив’язки до партій за всю історію; це не фізична нестача.`,
    });
    causes.sort((a, b) => a.priority - b.priority);
    return { item, causes, priority: causes[0]?.priority ?? 5 };
  }).filter(row => row.causes.length > 0)
    .sort((a, b) => a.priority - b.priority || a.item.productName.localeCompare(b.item.productName, 'uk'));
  const filtered = rows.filter(row => row.item.productName.toLocaleLowerCase('uk')
    .includes(search.trim().toLocaleLowerCase('uk')));
  const pageCount = Math.max(1, Math.ceil(filtered.length / 5));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleRows = filtered.slice(currentPage * 5, currentPage * 5 + 5);
  const urgentCount = rows.filter(row => row.priority <= 2).length;

  return <section className="inventory-attention" aria-labelledby="inventory-attention-title" aria-busy={loading}>
    <h3 id="inventory-attention-title">Складський центр уваги</h3>
    <details className="inventory-attention__help">
      <summary>Як читати ризики та залишки</summary>
      <p>Лише перегляд · документи до кінця поточного дня UTC; майбутні приходи не покривають поточні ризики.
      Залишок і нестача — після останньої закритої ревізії; партії та простежуваність — за всю історію, не перерахунок фізичного складу.</p>
    <p>Пріоритет: 1 — відкликання / прострочення / від’ємний залишок; 2 — карантин / непроведене списання;
      3 — термін до 14 днів / низький запас; 4 — простежуваність. Кількості різних причин не додаються.</p>
    </details>
    {loading ? <div role="status">Завантаження центру уваги…</div>
      : error ? <div role="alert">{error} Натисніть «Оновити» для повторної спроби.</div>
      : <>
        <div role="status">{items.length === 0 ? 'Дані складу відсутні.' : rows.length === 0 ? 'Товарів, що потребують уваги, немає.' : `Потребують уваги: ${rows.length} товарів (без повторного обліку причин).`}</div>
        {alerts.length > 0 && <p>Вартість під ризиком: {alerts.reduce((total, lot) => total + lot.valueAtRisk, 0).toFixed(2)} грн (кожна партія один раз, навіть з кількома причинами).</p>}
        {rows.length > 0 && <>
          <div className="inventory-attention__toolbar">
            <strong>Пріоритет 1–2: {urgentCount} товарів</strong>
            <button type="button" aria-expanded={expanded} aria-controls="inventory-attention-rows" onClick={() => setExpanded(value => !value)}>
              {expanded ? 'Згорнути список ризиків' : 'Розгорнути список ризиків'}
            </button>
          </div>
          {expanded && <div id="inventory-attention-rows">
            <label className="inventory-attention__search">Пошук серед ризиків
              <input value={search} onChange={event => { setSearch(event.target.value); setPage(0); }} placeholder="Знайти ризиковий товар..." />
            </label>
        <ol className="inventory-attention__list" start={currentPage * 5 + 1}>
          {visibleRows.map(({ item, causes, priority }) => <li key={item.productId} className="inventory-attention__row">
            <div>
              <strong>{item.productName}</strong> <span className="inventory-attention__priority">Пріоритет {priority}</span>
              <ul>{causes.map((cause, index) => <li key={index}>{cause.text}</li>)}</ul>
            </div>
            <div className="inventory-attention__actions">
              <button type="button" onClick={() => onLots(item.productId)} aria-label={`Відкрити партії: ${item.productName}`}>Відкрити партії</button>
              <button type="button" onClick={() => onMovements(item.productId)} aria-label={`Відкрити рухи: ${item.productName}`}>Відкрити рухи</button>
            </div>
          </li>)}
        </ol>
            {filtered.length === 0 && <p>За цим пошуком ризикових товарів немає.</p>}
            {filtered.length > 5 && <nav className="inventory-attention__toolbar" aria-label="Сторінки ризиків">
              <button type="button" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>Попередні ризики</button>
              <span>{currentPage + 1} / {pageCount} · знайдено {filtered.length}</span>
              <button type="button" disabled={currentPage + 1 >= pageCount} onClick={() => setPage(currentPage + 1)}>Наступні ризики</button>
            </nav>}
          </div>}
        </>}
      </>}
  </section>;
}
